import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Shield,
  Clock,
  Building2,
  Sparkles,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { VENDOR_RESULTS, AI_RESPONSE_HEADER, REPORT_STATS, type VendorResult } from './demoMockData';

interface VendorReportCardProps {
  isVisible: boolean;
}

/**
 * Vendor report with expandable sections, animated scores,
 * and clean light-theme design.
 */
export function VendorReportCard({ isVisible }: VendorReportCardProps) {
  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-4"
    >
      {/* Report Header */}
      <ReportHeader />

      {/* Vendor Cards */}
      <div className="space-y-3">
        {VENDOR_RESULTS.map((vendor, index) => (
          <VendorCard key={vendor.id} vendor={vendor} index={index} />
        ))}
      </div>

    </motion.div>
  );
}

function ReportHeader() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm"
    >
      {/* MCP Apps badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-2 mb-4"
      >
        <span className="px-3 py-1 bg-[#EAA059]/15 rounded-full text-xs text-[#EAA059] font-medium flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" />
          Interactive Report via MCP Apps
        </span>
      </motion.div>

      {/* AI Response */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-[#063F6B] text-sm leading-relaxed mb-4"
      >
        {AI_RESPONSE_HEADER}
      </motion.p>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="grid grid-cols-4 gap-3"
      >
        <StatBadge
          label="Vendors Analyzed"
          value={REPORT_STATS.vendorsAnalyzed.toLocaleString()}
          delay={0.6}
        />
        <StatBadge
          label="Matching"
          value={REPORT_STATS.matchingVendors.toString()}
          delay={0.7}
        />
        <StatBadge
          label="Top Picks"
          value={REPORT_STATS.topRecommendations.toString()}
          highlight
          delay={0.8}
        />
        <StatBadge
          label="Avg Match"
          value={`${REPORT_STATS.avgMatchScore}%`}
          delay={0.9}
        />
      </motion.div>
    </motion.div>
  );
}

function StatBadge({
  label,
  value,
  highlight = false,
  delay = 0
}: {
  label: string;
  value: string;
  highlight?: boolean;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      className={`
        text-center p-2 rounded-lg
        ${highlight ? 'bg-[#EAA059]/10 border border-[#EAA059]/20' : 'bg-gray-50'}
      `}
    >
      <div className={`text-lg font-bold ${highlight ? 'text-[#EAA059]' : 'text-[#063F6B]'}`}>
        {value}
      </div>
      <div className="text-xs text-gray-500">{label}</div>
    </motion.div>
  );
}

function VendorCard({ vendor, index }: { vendor: VendorResult; index: number }) {
  const [isExpanded, setIsExpanded] = useState(index === 0);
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8 + index * 0.15, duration: 0.4 }}
      className={`
        bg-white rounded-xl border overflow-hidden transition-all duration-300
        ${isExpanded ? 'border-[#EAA059]/40 shadow-sm' : 'border-gray-200 hover:border-gray-300'}
      `}
    >
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
      >
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-[#063F6B] font-semibold truncate">{vendor.name}</h4>
            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
              {vendor.pricingTier}
            </span>
          </div>
          <p className="text-sm text-gray-500 truncate">{vendor.tagline}</p>
        </div>

        {/* Match Score */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <MatchScoreCircle score={vendor.matchScore} delay={1 + index * 0.15} />
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </motion.div>
        </div>
      </button>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
              {/* Why Recommended */}
              <div className="bg-[#EAA059]/5 rounded-lg p-4 border border-[#EAA059]/15">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-[#EAA059]" />
                  <span className="text-sm font-medium text-[#EAA059]">Why Recommended</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {vendor.whyRecommended}
                </p>
              </div>

              {/* Capabilities & Features */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Capabilities */}
                <div>
                  <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Key Capabilities
                  </h5>
                  <div className="flex flex-wrap gap-1.5">
                    {vendor.capabilities.map((cap, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 * i }}
                        className="px-2 py-1 bg-gray-100 rounded text-xs text-[#063F6B]"
                      >
                        {cap}
                      </motion.span>
                    ))}
                  </div>
                </div>

                {/* Enterprise Features */}
                <div>
                  <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Enterprise & Compliance
                  </h5>
                  <div className="space-y-1.5">
                    {vendor.enterpriseFeatures.slice(0, 3).map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-emerald-700">
                        <CheckCircle2 className="w-3 h-3" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3 py-3 border-t border-gray-100">
                <QuickStat
                  icon={<Clock className="w-4 h-4" />}
                  label="Deployment"
                  value={vendor.deploymentTime}
                />
                <QuickStat
                  icon={<Building2 className="w-4 h-4" />}
                  label="Fortune 500"
                  value={`${vendor.fortune500Clients}+ clients`}
                />
                <QuickStat
                  icon={<Shield className="w-4 h-4" />}
                  label="SOC 2"
                  value="Type II"
                  highlight
                />
              </div>

              {/* Considerations */}
              <div>
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Considerations
                </h5>
                <div className="space-y-1.5">
                  {vendor.considerations.map((consideration, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-amber-700">
                      <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{consideration}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  if (vendor.id === 'glean') {
                    navigate('/reports/static/glean-demo');
                  }
                }}
                className="w-full py-2.5 bg-[#EAA059] hover:bg-[#d4914d] rounded-lg text-sm font-medium text-white flex items-center justify-center gap-2 transition-colors"
              >
                Start Evaluation
                <ExternalLink className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function MatchScoreCircle({ score, delay }: { score: number; delay: number }) {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="relative w-14 h-14">
      <svg className="w-full h-full transform -rotate-90">
        {/* Background circle */}
        <circle
          cx="28"
          cy="28"
          r={radius}
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
          className="text-gray-200"
        />
        {/* Progress circle */}
        <motion.circle
          cx="28"
          cy="28"
          r={radius}
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference * (1 - score / 100) }}
          transition={{ duration: 1, delay, ease: 'easeOut' }}
          strokeLinecap="round"
          className="text-[#EAA059]"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: delay + 0.5 }}
          className="text-sm font-bold text-[#EAA059]"
        >
          {score}%
        </motion.span>
      </div>
    </div>
  );
}

function QuickStat({
  icon,
  label,
  value,
  highlight = false
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className={`
      text-center p-2 rounded-lg
      ${highlight ? 'bg-emerald-50' : 'bg-gray-50'}
    `}>
      <div className={`flex justify-center mb-1 ${highlight ? 'text-emerald-600' : 'text-gray-500'}`}>
        {icon}
      </div>
      <div className={`text-xs font-medium ${highlight ? 'text-emerald-700' : 'text-[#063F6B]'}`}>
        {value}
      </div>
      <div className="text-xs text-gray-400">{label}</div>
    </div>
  );
}

